// client/src/components/register/ActivateAccount.js
import React, { useState, useEffect, useRef, useCallback } from 'react'; // useCallback!
import { useAuth } from '../../context/AuthContext'; // Import useAuth
import { useParams, useNavigate } from 'react-router-dom';
import LoadingSpinner from '../common/LoadingSpinner';
import { activateAccount } from '../../api/authApi';
import axios from 'axios';
import './ActivateAccount.css';
import { useTranslation } from 'react-i18next';

function ActivateAccount() {
    const { t, i18n } = useTranslation(); // Destructure BOTH t and i18n
    const { userId } = useParams();
    const navigate = useNavigate();
    const [code, setCode] = useState(['', '', '', '', '', '']);
    const [timer, setTimer] = useState(null); // Start with null.
    const [error, setError] = useState('');
    const [message, setMessage] = useState('');
    const [resendDisabled, setResendDisabled] = useState(true); // Start DISABLED
    const inputRefs = useRef([]);
    const timerRef = useRef(null); // Holds the interval ID

    const { isLoggedIn, loading } = useAuth(); // Get isLoggedIn AND loading

    // --- Helper Function: Fetch and Start Timer (using useCallback) ---
    const fetchRemainingTime = useCallback(async () => {
        try {
            const response = await axios.get(`/api/auth/activation-time/${userId}`);
            const remainingTime = response.data.remainingTime;
            setTimer(remainingTime);

            // Clear any existing timer BEFORE setting up a new one
            if (timerRef.current) {
                clearInterval(timerRef.current);
                timerRef.current = null; // Good practice
            }

            if (remainingTime > 0) {
                setResendDisabled(true); // Disable resend if time remains

                // Set up interval to decrement timer every second
                timerRef.current = setInterval(() => {  // Store interval ID
                    setTimer((prevTimer) => {
                         if (typeof prevTimer !== 'number') {
                            return prevTimer;  //Don't decrement if it null or invalid
                          }
                        const newTimer = prevTimer - 1;
                        if (newTimer <= 0) {
                            clearInterval(timerRef.current); // Clear interval
                            timerRef.current = null; // Reset ref
                            setResendDisabled(false); // Enable resend
                            return 0; // Ensure timer doesn't go negative
                        }
                        return newTimer;
                    });
                }, 1000);
            } else {
                // Expired or no token
                setResendDisabled(false); // Enable resend button.
            }
        } catch (error) {
            console.error("Error fetching remaining time:", error);
            if (error.response && error.response.status === 404) {
                 setError(t('activation.errors.noActivationCode')); // Specific error
                 setResendDisabled(false); // Enable resend button.
            } else {
                setError(t('activation.errors.activationError'));
            }
            setTimer(0); // Ensure timer is 0 on error.
        }
    }, [userId, t]); // Dependencies for useCallback


    useEffect(() => {
        if (!userId || isNaN(parseInt(userId)) || isLoggedIn) {
            navigate('/not-found');
            return;
        }

        const fetchUserStatus = async () => {
            try {
                const response = await axios.get(`/api/auth/status/${userId}`);
                const { status } = response.data;

                if (status === 'active') {
                    navigate('/login');
                    return;
                } else if (status !== 'deactivated') {
                    setError(t('activation.errors.accountNotActive'));
                    return;
                }
                // Fetch initial time, and focus.
                await fetchRemainingTime();
                 if (inputRefs.current[0]) {
                    inputRefs.current[0].focus();
                }


            } catch (error) {
               console.error("Error fetching user status:", error);
                if (error.response && error.response.status === 404) {
                    navigate('/not-found');
                } else {
                    setError(t('activation.errors.activationError'));
                }
            }
        };

        fetchUserStatus();


        // Cleanup function: clear interval on unmount
        return () => {
            if (timerRef.current) {
                clearInterval(timerRef.current);
                timerRef.current = null;
            }
        };

    }, [navigate, userId, isLoggedIn, t, fetchRemainingTime]); // Include fetchRemainingTime


    const handleChange = (index, value) => {
        if (/^\d?$/.test(value)) {
            const newCode = [...code];
            newCode[index] = value;
            setCode(newCode);

            if (value && index < 5 && inputRefs.current[index + 1]) {
                inputRefs.current[index + 1].focus();
            }
            if (!value && index > 0 && inputRefs.current[index - 1]) {
                inputRefs.current[index - 1].focus();
            }
        }
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        setError('');
        setMessage('');

        const fullCode = code.join('');

        if (fullCode.length !== 6) {
            setError(t('activation.errors.invalidActivationCode'));
            return;
        }

        try {
            // const response = await axios.post('/api/auth/activate', { userId, code: fullCode });
            const response = await activateAccount(userId, fullCode); // Use the API function

            // --- SUCCESS HANDLING ---
            console.log("RActivation successful:", response); // Log success response
            // Check for expected data in the success response
            if (response) {
                sessionStorage.removeItem('userId');
                sessionStorage.removeItem('expiry');
                sessionStorage.removeItem('sessionToken');
                setTimeout(() => {
                    navigate('/login');
                }, 3000); // Redirect after 3 seconds
            } else {
                setError('Unexpected error during activation.');
                console.error("Unexpected server response:", response);
            }

        } catch (err) {
            if (err.response && err.response.status === 410) {
                setError(t('activation.errors.activationCodeExpired'));
                setTimer(0);
                setResendDisabled(false); // Enable resend
            } else if (err.response && err.response.status === 404) {
                setError(t('activation.errors.userNotFound'));
            } else if (err.response) {
                setError(err.response.data.message);
            } else {
                setError(t('activation.errors.activationError'));
            }
        }
    };



    const handleResendCode = async () => {
        setError('');
        setMessage('');
        setResendDisabled(true); // Disable immediately on click

        try {
            const language = i18n.language; // Correctly using i18n
            const response = await axios.post('/api/auth/resend-code', { userId, language });
            setMessage(response.data.message);
            // Fetch the new remaining time (and restart timer)
            await fetchRemainingTime();
            setCode(['', '', '', '', '', '']);

        } catch (err) {
            if (err.response) {
                setError(err.response.data.message);
            } else {
                setError(t('activation.errors.resendCodeError'));
            }
             setResendDisabled(false); // Re-enable on error
        }
    };

    
    if (loading) {
        return <LoadingSpinner />; // Use the LoadingSpinner
    }

    return (
        <div className="activation-container">
            <h2>{t('activation.activateAccount')}</h2>
            <p>{t('activation.enterCode')}</p>

            <form onSubmit={handleSubmit} className="activation-form">
                <div className="code-inputs">
                    {code.map((digit, index) => (
                        <React.Fragment key={index}>
                            <input
                                type="text"
                                maxLength="1"
                                value={digit}
                                onChange={(e) => handleChange(index, e.target.value)}
                                ref={(el) => (inputRefs.current[index] = el)}
                                className="code-input"
                            />
                            {index < code.length - 1 && (<span className="separator">-</span>)}
                        </React.Fragment>
                    ))}
                </div>

                {timer !== null && timer > 0 ? (
                    <p>
                        {t('activation.timeRemaining')} <span className="timer">{timer}</span> {t('activation.seconds')}
                    </p>
                ) : (
                   timer !== null && <p>{t('activation.codeExpired')}</p>
                )}

                {error && <p className="error-message">{error}</p>}
                {message && <p className="success-message">{message}</p>}

                <button type="submit" className="activate-button" disabled={timer === null || timer === 0}>
                    {t('activation.activate')}
                </button>
                <button
                    type="button"
                    className="resend-button"
                    onClick={handleResendCode}
                    disabled={resendDisabled}
                >
                    {t('activation.resendCode')}
                </button>
            </form>
        </div>
    );
}

export default ActivateAccount;